import { push } from 'react-router-redux';
import type { IToastMessage } from '../helpers/Toastr';
import { toastr } from '../helpers/Toastr';

import { fetchWithRefresh } from '../fetch';
import { LocalStorage } from '../helpers';
import route from '../helpers/Routes';

export const UNAUTHORIZED_REQUEST = '@fa:UNAUTHORIZED';

export interface IFetchActionConfig {
	url: string;
	startAction: string;
	successAction?: string;
	errorAction?: string;
	headers?: Headers;
	method?: string;
	body?: string | FormData;
	validationFunc?: (dispatch: Function, getState: Function) => boolean;
	startActionPayload?: any;
	payloadTransformer?: Function;
	onSuccessAction?: (
		data,
		dispatch,
		getState,
		httpResponseStatus,
		value
	) => void;
	onAfterSuccess?: (
		data,
		dispatch,
		getState,
		httpResponseStatus,
		value
	) => void;
	onFailure?: (valueResponse, dispatch, getState) => void;
	cache?: 'default' | 'no-store' | 'reload' | 'no-cache' | 'force-cache';
	redirectOnUnauthorized?: boolean;
	unauthorizedUrl?: string;
	redirectOnNotFound?: boolean;
	jsonResponse?: boolean;
}

/**
 * @deprecated Use `@reduxjs/toolkit`
 */
function CreateFetchAction(
	config: IFetchActionConfig
): (dispatch, getState) => void {
	const startAction = config.startAction;
	const successAction = config.successAction || startAction + '_SUCCESS';
	const errorAction = config.errorAction || startAction + '_ERROR';

	const unauthorizedUrl =
		config.unauthorizedUrl ||
		(LocalStorage.isSingleSignOnUser()
			? route.SSOExpiredSession()
			: route.logout());
	let redirectOnUnauthorized = true;
	let redirectOnNotFound =
		typeof config.redirectOnNotFound === 'undefined'
			? true
			: config.redirectOnNotFound;

	const url = config.url;
	let jsonResponse = true;

	if (config.redirectOnUnauthorized != null) {
		redirectOnUnauthorized = config.redirectOnUnauthorized;
	}

	if (config.redirectOnNotFound != null) {
		redirectOnNotFound = config.redirectOnNotFound;
	}

	if (config.jsonResponse != null) {
		jsonResponse = config.jsonResponse;
	}

	let headers = config.headers;

	if (!headers) {
		headers = new Headers();
		headers.append('Accept', 'application/json');

		if (!(config.body instanceof FormData)) {
			headers.append('Content-Type', 'application/json');
		}
	}

	const options: RequestInit = {
		method: config.method || 'GET',
		headers,
		cache: config.cache || 'no-cache',
		credentials: 'include',
	};

	if (config.body) {
		options.body = config.body;
	}

	return (dispatch, getState) => {
		if (config.validationFunc && !config.validationFunc(dispatch, getState)) {
			return;
		}

		dispatch({
			type: startAction,
			payload: config.startActionPayload,
		});

		const handleSuccess = (
			data: any,
			httpResponse: Response,
			value: any
		): any => {
			if (config.onSuccessAction) {
				config.onSuccessAction(
					data,
					dispatch,
					getState,
					httpResponse.status,
					value
				);
			}
			if (config.payloadTransformer) {
				data = config.payloadTransformer(data);
			}
			dispatch({
				type: successAction,
				payload: data,
			});
			if (config.onAfterSuccess) {
				config.onAfterSuccess(
					data,
					dispatch,
					getState,
					httpResponse.status,
					value
				);
			}
			return {
				value: data,
				httpResponse,
			};
		};

		const dispatchToast = (title: string, body: string): void => {
			const toastMessage: IToastMessage = {
				title,
				body,
			};
			toastr.error(toastMessage);
		};

		const reduceArrayToString = (prev: string, curr: string): string => {
			return prev + ', ' + curr;
		};

		const handleErrorStateOrThrow = (
			value: any,
			httpResponse: Response
		): any => {
			let errorsPayload = null;
			if (value && value.errors) {
				errorsPayload = value.errors.reduce(reduceArrayToString);
			}

			if (httpResponse.status === 400) {
				if (typeof value === 'string') {
					dispatch({
						type: errorAction,
						payload: value,
					});
				} else {
					if (value.statusCode >= 200 && value.statusCode <= 499) {
						if (value.statusCode === 201) {
							dispatch(push('/customers'));
						} else {
							dispatchToast('Validation Error', errorsPayload);
							dispatch({ type: errorAction, payload: errorsPayload });
						}
					}
				}
			} else if (
				(httpResponse.status === 401 || httpResponse.status === 403) &&
				redirectOnUnauthorized
			) {
				dispatch({
					type: UNAUTHORIZED_REQUEST,
					payload: true,
				});
				window.location.pathname = unauthorizedUrl;
			} else if (httpResponse.status === 404 && redirectOnNotFound) {
				if (window.history) {
					window.history.replaceState({}, 'Not Found', '/notfound');
					window.history.go(0);
				} else {
					window.location.pathname = '/notfound';
				}
			} else {
				return new Promise((resolve, reject) => {
					reject(value);
				});
			}
			return {
				value,
				httpResponse,
			};
		};

		fetchWithRefresh(url, options)
			.then((response) => {
				const tryHarder = response.clone();
				const promise =
					jsonResponse || !response.ok
						? response.json().catch(() => tryHarder.text())
						: response.text();

				return promise.then((value) => {
					if (response.ok) {
						if (
							jsonResponse &&
							(response.status === 200 || response.status === 201)
						) {
							return handleSuccess(value.value, response, value);
						} else if (response.status === 200) {
							return handleSuccess(value, response, value);
						} else {
							return handleSuccess(null, response, value);
						}
					} else {
						return handleErrorStateOrThrow(value, response);
					}
				});
			})
			.catch((valueResponse) => {
				if (config.onFailure) {
					config.onFailure(valueResponse, dispatch, getState);
				} else {
					if (typeof valueResponse === 'string') {
						dispatchToast('Error', 'Something went wrong with your request.');
						dispatch({ type: errorAction, payload: valueResponse });
					} else {
						if (valueResponse.statusCode >= 900) {
							dispatchToast('Error', 'Something went wrong with your request.');
							dispatch({
								type: errorAction,
								payload: valueResponse.errors.reduce(reduceArrayToString),
							});
						}
					}
				}
			});
	};
}

export { CreateFetchAction };
